import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { CreateApplicationError } from '~widgets/create-application-error/create-application-error';

import { analytics } from '~entities/amplitude-analytics';
import type { Application } from '~entities/application';
import {
  useCreateOrFindApplicationMutation,
  useSubmitApplicationMutation,
} from '~entities/application';
import type { SubmitAction } from '~entities/application';
import { featureFlags } from '~entities/application/lib/skip-validation-flags';
import {
  isShortApplicationFlow,
  isOnboarding,
} from '~entities/application/lib/status-checkers';
import { userPhoneAtom } from '~entities/auth';
import { useGetOrderByIdQuery, orderAtom } from '~entities/order';
import { useGetPersonQuery } from '~entities/person';

import { useOnMountEffect } from '~shared/hooks';
import errorImage from '~shared/images/error.webp';
import { nonNullableValue } from '~shared/types/non-nullable-value';
import { HeaderWithLogo } from '~shared/ui/header-with-logo';
import { Loader } from '~shared/ui/loader';
import { Result } from '~shared/ui/result';
import { Screen } from '~shared/ui/screen';

import { setTag } from '@breeze/web-lib-sentry';
import { useAtomValue, useSetAtom } from 'jotai';

import { LoanApplicationForm } from './form/application-form';

import {
  recoverApplicationScreenValues,
  recoverPersonScreenValues,
  showErrorToast,
} from '../lib';
import { recoverInitialStep } from '../lib/recover-initial-step';
import {
  initializeFormAtom,
  initializeFormSuccessAtom,
} from '../model/form-atoms';

export const OrderPage: React.FC = () => {
  const navigate = useNavigate();
  const userPhone = useAtomValue(userPhoneAtom);
  const { orderId } = useParams();
  const [createOrFindError, setCreateOrFindError] = useState<Error>();

  useOnMountEffect(() => {
    window.clarity?.('set', 'order_id', orderId);
    setTag('orderId', orderId);
  });

  const {
    data: order,
    isLoading: isOrderLoading,
    isSuccess,
    error: getOrderError,
  } = useGetOrderByIdQuery(orderId, {
    enabled: !!orderId,
  });
  const isOrderFound = isSuccess && order.orderItems.length > 0;

  const {
    mutateAsync: createApplicationAsync,
    data: application,
    isSuccess: isApplicationCreated,
  } = useCreateOrFindApplicationMutation();
  const { mutateAsync: submitAsync } = useSubmitApplicationMutation({
    onError: showErrorToast,
  });

  const { data: person, isSuccess: isPersonLoaded } = useGetPersonQuery(
    nonNullableValue(application?.id),
    {
      enabled: !!application?.id,
    }
  );

  const [initializeForm, recoverForm, setOrder] = [
    useSetAtom(initializeFormAtom),
    useSetAtom(initializeFormSuccessAtom),
    useSetAtom(orderAtom),
  ];

  useEffect(() => {
    if (order) {
      setOrder(order);
    }
  }, [order, setOrder]);

  // initialize form if session has been restored
  const handleInitialize = async (
    applicationData: Partial<Application>,
    skipValidation?: boolean
  ) => {
    initializeForm();

    try {
      const result = await createApplicationAsync({
        orderId: nonNullableValue(orderId),
        requestedProduct: applicationData.requestedProduct,
        featureFlags: skipValidation ? featureFlags : undefined,
        ...(applicationData.offer && { offer: applicationData.offer }),
      });

      window.clarity?.('set', 'application_id', result.id);
      setTag('applicationId', result.id);
      setTag('flowType', result.flowType);

      analytics.track.applicationCreated(result.id, order?.successUrl);
      // submitted application -> show application status screen
      if (!isOnboarding(result)) {
        navigate(`/applications/${result.id}`, { replace: true });
      }

      if (isShortApplicationFlow(result)) {
        navigate(`/applications/${result.id}`);
      }
    } catch (error: any) {
      setCreateOrFindError(error);
    }
  };

  const handleSubmit = async (submitAction?: SubmitAction) => {
    const { id, redirectUrl } = nonNullableValue(application);
    await submitAsync({
      applicationId: id,
      submitAction,
    });

    if (submitAction === 'REJECT' && redirectUrl) {
      window.open(redirectUrl, '_self');
      return;
    }

    navigate(`/applications/${id}`);
  };

  useEffect(() => {
    if (
      isApplicationCreated &&
      isPersonLoaded &&
      !isShortApplicationFlow(application)
    ) {
      const recoveredApplication = recoverApplicationScreenValues(application);
      const recoveredPerson = recoverPersonScreenValues(person);

      recoverForm({
        values: { ...recoveredPerson, ...recoveredApplication },
        recoveredInitialStep: recoverInitialStep(
          application.id,
          person.metaData
        ),
      });
    }
  }, [
    isApplicationCreated,
    application,
    isPersonLoaded,
    person,
    recoverForm,
    initializeForm,
  ]);

  if (isOrderLoading) {
    return (
      <Screen header={<HeaderWithLogo />}>
        <Loader centered />
      </Screen>
    );
  }

  if (getOrderError) {
    return (
      <Screen header={<HeaderWithLogo />}>
        <Result
          image={errorImage}
          title="We can't find your order"
          subtitle="Please, check the&nbsp;link"
        />
      </Screen>
    );
  }

  if (createOrFindError) {
    return (
      <Screen header={<HeaderWithLogo />}>
        <div className="mt-20">
          <CreateApplicationError
            error={createOrFindError}
            backLink={order?.failUrl}
          />
        </div>
      </Screen>
    );
  }

  if (isOrderFound) {
    return (
      <LoanApplicationForm
        application={application}
        order={nonNullableValue(order)}
        onInitialize={handleInitialize}
        clientPhone={userPhone ?? undefined}
        onSubmit={handleSubmit}
      />
    );
  }

  return <Screen header={<HeaderWithLogo />}>Something went wrong</Screen>;
};
